import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Sparkles, Gauge, ArrowRight, ShieldCheck, BarChart3 } from "lucide-react";
import NavBar from "@/components/NavBar";
import Footer from "@/components/Footer";
import StockSearch from "@/components/StockSearch";
import PickCard from "@/components/PickCard";
import HeroCapabilityStrip from "@/components/HeroCapabilityStrip";
import HowItWorks from "@/components/HowItWorks";
import LandingFAQ from "@/components/LandingFAQ";
import PortfolioShowcase from "@/components/PortfolioShowcase";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { useTranslation } from "@/lib/i18n";
import {
  methodologyShortLabel,
  conflictsLabel,
  correctionsLabel,
} from "@/lib/researchTransparencyCopy";
import { stashTickerProvenance } from "@/lib/ticker_provenance";
import { startAnalyze } from "@/lib/startAnalyze";
import { useCompanyIdentities } from "@/lib/useCompanyIdentities";
import { Disclaimer } from "@/components/Disclaimer";

export default function Landing() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [picks, setPicks] = useState([]);
  const [asOf, setAsOf] = useState("");
  const [loadingPicks, setLoadingPicks] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoadingPicks(true);
        const res = await api.get("/picks");
        if (!cancelled) {
          setPicks(res.data.picks || []);
          setAsOf(res.data.as_of || "");
        }
      } catch {
        if (!cancelled) setPicks([]);
      } finally {
        if (!cancelled) setLoadingPicks(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Lead pick: prefer the explicit top pick, else first opportunity, else first.
  const leadPick = picks.find((p) => p.is_top_pick) || picks.find((p) => p.is_opportunity) || picks[0];
  const restPicks = picks.filter((p) => p !== leadPick);
  const firstOpportunity = picks.find((p) => p.is_opportunity);

  // Phase 8.LP.LR — batched cache-only identity lookup for every
  // pick on the AI Market Snapshot. Fires a single GET request per
  // unique ticker set; falls back to initials/ticker text when no
  // identity is cached (no FMP round-trip is ever triggered from
  // the landing page).
  const pickTickers = useMemo(() => picks.map((p) => p.ticker), [picks]);
  const { getIdentity } = useCompanyIdentities(pickTickers);

  return (
    <div className="min-h-screen flex flex-col overflow-x-clip">
      <NavBar showSearch={false} />

      {/* Hero — compressed mobile rhythm. One intentional cinematic glow. */}
      <section className="relative" data-testid="landing-hero">
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          <div className="noise" />
          {/* Single, intentional center glow that anchors the headline.
              On mobile we tuck it slightly higher so it sits behind the H1.
              Phase 7.UI.2 — the glow colour is driven by `--hero-glow`
              so the light theme can collapse it into a barely-visible
              warm wash instead of the SaaS-template cyan haze. */}
          <div
            className="absolute -top-24 sm:-top-40 left-1/2 -translate-x-1/2 w-[760px] sm:w-[1100px] h-[520px] sm:h-[820px] rounded-full"
            style={{ background: "radial-gradient(closest-side, var(--hero-glow), transparent 70%)" }}
          />
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-3 sm:pt-9 pb-5 sm:pb-12 relative">
          <div className="text-center max-w-3xl mx-auto">
            <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
              {/* Hero credibility row — one standalone rotating globe +
                  a primary + secondary text statement. Owner-approved
                  refinement of the previous four-pill strip. The old
                  "AI valuation engine \u00B7 live picks" badge was removed
                  as part of the same correction. */}
              <div className="flex justify-center mb-3 sm:mb-5 px-1">
                <HeroCapabilityStrip />
              </div>
              <h1
                className="font-display mt-2 sm:mt-6 text-[30px] leading-[1.04] sm:text-5xl lg:text-6xl font-semibold tracking-tight max-w-[15ch] sm:max-w-none mx-auto text-balance"
                style={{ textShadow: "var(--hero-text-shadow)" }}
              >
                {t("landing.hero_title_l1")}<br className="hidden sm:block" /> {t("landing.hero_title_l2")}
              </h1>
              {/* Sub-pass 2A follow-up (2026-02-17) — the subhero is
                  now a two-sentence value proposition (~190 chars
                  EN, ~200 DE).  Widen the desktop container from
                  `max-w-2xl` to `max-w-3xl` so the copy wraps
                  naturally to ~2 lines without pushing the hero
                  container excessively wide; mobile stays at
                  `max-w-[36ch]` so line length remains balanced. */}
              <p className="mt-2 sm:mt-3 text-[13.5px] leading-[1.5] sm:text-lg sm:leading-relaxed text-muted-foreground/80 max-w-[36ch] sm:max-w-3xl mx-auto px-1"
                 data-testid="landing-hero-subtitle">
                {t("landing.hero_subtitle")}
              </p>
            </motion.div>
            {/* Search sits IMMEDIATELY under the subtitle — no breathing room games. */}
            <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.1 }} className="mt-3 sm:mt-7">
              <div className="max-w-xl mx-auto glow-ring rounded-xl">
                <StockSearch testIdPrefix="hero-search" autoFocus placeholder={t("landing.hero_search_placeholder")} />
              </div>
              <div className="mt-2.5 sm:mt-4">
                <Button
                  data-testid="hero-analyze-first-stock-button"
                  onClick={() => {
                    const t = firstOpportunity?.ticker || "NVDA";
                    startAnalyze({ navigate, ticker: t, source: "landing:cta_hero_analyze_first" });
                  }}
                  className="shadow-[var(--app-shadow-elev-1)] w-full sm:w-auto h-10 sm:h-11 px-5 sm:px-6 text-sm sm:text-base font-medium"
                >
                  {t("landing.hero_cta")} <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* AI Market Snapshot — mobile uses a Lead card + 3 compact rows pattern. */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-4 sm:mt-7 relative z-0 min-w-0 w-full" data-testid="landing-picks-section">
        {/* Section header: tighter on mobile, more intentional eyebrow */}
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-2 sm:gap-3 mb-3 sm:mb-3">
          <div>
            <div className="flex items-center gap-2">
              <span
                data-testid="picks-eyebrow"
                className="picks-eyebrow text-[10px] sm:text-[11px] uppercase tracking-[0.18em] text-muted-foreground/85 font-medium"
              >
                {t("landing.picks_eyebrow")}
              </span>
            </div>
            <h2 className="font-display text-[22px] sm:text-3xl tracking-tight leading-tight mt-1" data-testid="picks-section-title">{t("landing.picks_title")}</h2>
            <p
              data-testid="picks-subtitle"
              className="picks-subtitle text-[12.5px] sm:text-sm leading-[1.5] text-muted-foreground/85 mt-1 max-w-3xl"
            >
              {t("landing.picks_subtitle")}
            </p>
          </div>
          <div className="flex items-center justify-between sm:justify-end gap-3">
            {asOf ? <span className="text-[11px] sm:text-xs text-muted-foreground tabular">{t("landing.picks_as_of", { time: asOf })}</span> : null}
          </div>
        </div>

        {/* Legend — desktop only; mobile gets a quieter single-line caption to save height */}
        <div className="hidden sm:flex flex-wrap items-center gap-1.5 sm:gap-2 mb-4 sm:mb-5" data-testid="picks-legend">
          <LegendChip color="#3FAE89" bg="rgba(63,174,137,0.14)" border="rgba(63,174,137,0.40)" label={t("landing.legend_opportunity")} desc={t("landing.legend_opportunity_desc")} testKey="opportunity" />
          <LegendChip color="#E0B341" bg="rgba(224,179,65,0.12)" border="rgba(224,179,65,0.40)" label={t("landing.legend_neutral")} desc={t("landing.legend_neutral_desc")} testKey="neutral" />
          <LegendChip color="#E5484D" bg="rgba(229,72,77,0.12)" border="rgba(229,72,77,0.42)" label={t("landing.legend_risk")} desc={t("landing.legend_risk_desc")} testKey="risk" />
          <span
            data-testid="picks-legend-helper"
            className="picks-legend-helper basis-full sm:basis-auto text-[11px] text-muted-foreground/80 sm:ml-1 mt-0.5 sm:mt-0"
          >
            {t("landing.legend_balanced")}
          </span>
        </div>
        <div className="sm:hidden mb-3 inline-flex items-center gap-2 text-[10.5px] text-muted-foreground/75">
          <LegendDot color="#3FAE89" /> {t("landing.legend_opportunity")}
          <LegendDot color="#E0B341" /> {t("landing.legend_neutral")}
          <LegendDot color="#E5484D" /> {t("landing.legend_risk")}
        </div>

        {loadingPicks ? (
          <>
            {/* Mobile loading: lead skeleton + 3 row skeletons */}
            <div className="sm:hidden space-y-2.5" data-testid="picks-loading">
              <div className="relative overflow-hidden rounded-2xl border border-border/70 bg-card/40 p-4 h-64">
                <div className="scan-sweep" />
                <div className="h-3 w-24 rounded bg-muted/40" />
                <div className="mt-3 h-9 w-32 rounded bg-muted/40" />
                <div className="mt-4 h-16 rounded bg-muted/40" />
              </div>
              {[0, 1, 2].map((i) => (
                <div key={i} className="relative overflow-hidden rounded-xl border border-border/70 bg-card/40 px-3.5 py-3 h-[68px]">
                  <div className="scan-sweep" />
                  <div className="h-3 w-20 rounded bg-muted/40" />
                  <div className="mt-2 h-3 w-32 rounded bg-muted/40" />
                </div>
              ))}
            </div>
            {/* Desktop loading: original 4-grid */}
            <div className="hidden sm:grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3.5 sm:gap-4">
              {[0, 1, 2, 3].map((i) => (
                <div key={i} className="relative overflow-hidden rounded-2xl border border-border/70 bg-card/40 p-5 h-72">
                  <div className="scan-sweep" />
                  <div className="h-3 w-24 rounded bg-muted/40" />
                  <div className="mt-3 h-7 w-32 rounded bg-muted/40" />
                  <div className="mt-3 h-3 w-40 rounded bg-muted/40" />
                  <div className="mt-6 h-20 rounded bg-muted/40" />
                </div>
              ))}
            </div>
          </>
        ) : (
          <>
            {/* MOBILE: Lead featured card + compact row list */}
            <div className="sm:hidden min-w-0" data-testid="picks-grid-mobile">
              {leadPick ? (
                <div className="mb-3">
                  <PickCard pick={leadPick} identity={getIdentity(leadPick.ticker)} index={0} variant="featured" />
                </div>
              ) : null}
              {restPicks.length > 0 ? (
                <>
                  <div className="flex items-center gap-2 mt-4 mb-2 px-1">
                    <span className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground/70 font-medium">More signals</span>
                    <span className="flex-1 h-px bg-gradient-to-r from-border/60 via-border/30 to-transparent" />
                  </div>
                  <div className="space-y-2">
                    {restPicks.map((p, i) => (
                      <PickCard key={p.ticker} pick={p} identity={getIdentity(p.ticker)} index={i + 1} variant="row" />
                    ))}
                  </div>
                </>
              ) : null}
            </div>
            {/* DESKTOP: original grid, but lead pick gets featured treatment */}
            <div className="hidden sm:grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3.5 sm:gap-4" data-testid="picks-grid">
              {picks.map((p, i) => (
                <PickCard key={p.ticker} pick={p} identity={getIdentity(p.ticker)} index={i} variant={p === leadPick ? "featured" : "default"} />
              ))}
            </div>
          </>
        )}

        <div className="mt-3 text-[11px] text-muted-foreground/70" data-testid="picks-disclaimer">
          {t("landing.picks_disclaimer")}
        </div>
      </section>

      {/* How it works — three-step user journey (Search → Valuation →
          Conviction). Sits between the AI Market Snapshot and the
          existing trust strip. Purely explanatory: reuses the Hero
          stock-search flow via a single understated CTA. */}
      <HowItWorks />

      {/* Trust strip — airy inline pill row on mobile, full grid on desktop.
          The valuation subtitle routes through the CENTRALIZED
          `methodologyShortLabel()` mapping (WS-7 methodology v1) so
          Landing, AnalyzeLanding, TransparencyPanel and the central
          Research Transparency page share the exact same wording.
          `landing.trust_valuation_sub` is kept only as a fallback for
          the (unrelated) test doubles that stub the i18n dictionary. */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-12 sm:mt-12" data-testid="landing-trust">
        <h2
          data-testid="landing-trust-heading"
          className="mt-2 mb-6 sm:mb-8 text-2xl sm:text-3xl font-semibold tracking-tight text-foreground text-center max-w-2xl mx-auto"
        >
          <span className="md:block" data-testid="landing-trust-heading-line1">
            {t("landing.trust_footnote_line1")}
          </span>
          {" "}
          <span className="md:block" data-testid="landing-trust-heading-line2">
            {t("landing.trust_footnote_line2")}
          </span>
        </h2>
        <ul className="md:hidden grid grid-cols-2 gap-x-3 gap-y-3.5 px-1">
          <TrustInline icon={BarChart3}  title={t("landing.trust_cashflow_title")}      subtitle={t("landing.trust_cashflow_sub")} />
          <TrustInline icon={ShieldCheck} title={t("landing.trust_valuation_title")}     subtitle={t("landing.trust_valuation_sub")} />
          <TrustInline icon={Gauge}       title={t("landing.trust_profitability_title")} subtitle={t("landing.trust_profitability_sub")} />
          <TrustInline icon={Sparkles}    title={t("landing.trust_ai_title")}            subtitle={t("landing.trust_ai_sub")} />
        </ul>
        <div className="hidden md:grid rounded-2xl border border-border/70 bg-card/40 p-5 grid-cols-4 gap-4">
          <TrustItem icon={BarChart3} title={t("landing.trust_cashflow_title")} subtitle={t("landing.trust_cashflow_sub")} />
          <TrustItem icon={ShieldCheck} title={t("landing.trust_valuation_title")} subtitle={t("landing.trust_valuation_sub")} />
          <TrustItem icon={Gauge} title={t("landing.trust_profitability_title")} subtitle={t("landing.trust_profitability_sub")} />
          <TrustItem icon={Sparkles} title={t("landing.trust_ai_title")} subtitle={t("landing.trust_ai_sub")} />
        </div>
      </section>

      {/* Landing Page Polish 4 — Portfolio Showcase / Preview Loop.
          Replaces the previous three standalone product cards
          (AI Opportunity Score / Fair Value / Instant Insight) which
          duplicated concepts already covered in the three-step
          "How it works" section. */}
      <PortfolioShowcase />

      {/* Landing Page Polish 3 — bilingual FAQ, placed directly
          before the final CTA. */}
      <LandingFAQ />

      {/* CTA — refined atmospheric transition. The blue band is now a
          localized radial halo behind the CTA card that fades naturally
          into the page black without any visible horizontal cutoff. */}
      <section className="relative mt-20 sm:mt-28 isolate" data-testid="landing-cta">
        {/* Localized ambient wash — constrained to a narrow column
            centered behind the CTA card so it reads as a soft halo,
            never as a full-width band.  The radial gradient itself
            stays anchored at 50% 55% as before (kept as-is for
            regression parity), but the container is now
            ~900px wide / centered rather than viewport-wide.
            Intensity softened by ~20%. */}
        <div className="pointer-events-none absolute left-1/2 -translate-x-1/2 -top-40 sm:-top-52 w-[720px] sm:w-[900px] h-[420px] sm:h-[520px]"
             data-testid="landing-cta-halo"
             style={{ background: "radial-gradient(60% 65% at 50% 55%, rgba(49,130,206,0.13), rgba(49,130,206,0.04) 55%, transparent 78%)" }} />
        <div className="pointer-events-none absolute left-1/2 -translate-x-1/2 -bottom-10 w-[520px] sm:w-[680px] h-40"
             style={{ background: "radial-gradient(50% 100% at 50% 0%, rgba(49,130,206,0.05), transparent 70%)" }} />

        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div
            className="relative overflow-hidden rounded-[28px] sm:rounded-[32px] border border-[rgba(49,130,206,0.35)] bg-card/60 backdrop-blur px-5 sm:px-14 py-12 sm:py-16 text-center"
            style={{
              boxShadow: [
                "0 0 0 1px rgba(49,130,206,0.12) inset",
                "0 1px 0 rgba(255,255,255,0.06) inset",
                "0 30px 90px -40px rgba(49,130,206,0.55)",
                "0 0 60px -18px rgba(49,130,206,0.35)",
              ].join(", "),
            }}
          >
            <div className="absolute inset-0 pointer-events-none">
              <div className="absolute -top-32 left-1/2 -translate-x-1/2 w-[520px] sm:w-[820px] h-[340px] rounded-full"
                   style={{ background: "radial-gradient(closest-side, rgba(49,130,206,0.22), transparent 72%)", filter: "blur(2px)" }} />
              <div className="absolute -bottom-24 -right-12 sm:-right-20 w-[280px] sm:w-[420px] h-[220px] rounded-full"
                   style={{ background: "radial-gradient(closest-side, rgba(49,130,206,0.14), transparent 75%)" }} />
              <div className="absolute top-0 left-8 right-8 h-px" style={{ background: "linear-gradient(90deg, transparent, rgba(49,130,206,0.6), transparent)" }} />
            </div>
            <div className="relative">
              <span className="inline-flex items-center gap-1.5 rounded-full border border-[rgba(49,130,206,0.32)] bg-[rgba(49,130,206,0.08)] px-2.5 py-1 text-[10.5px] uppercase tracking-[0.20em]" style={{ color: "#3182CE" }}>
                <Sparkles className="w-3 h-3" /> Start in 30 seconds
              </span>
              <h2 className="mt-5 sm:mt-6 font-display text-[32px] leading-[1.06] sm:text-[46px] sm:leading-[1.08] tracking-tight max-w-[18ch] sm:max-w-none mx-auto"
                  style={{ textShadow: "0 0 48px rgba(49,130,206,0.30)" }}>
                {t("landing.cta_title")}
              </h2>
              <p className="mt-4 text-[14px] sm:text-[15.5px] leading-[1.55] text-muted-foreground/85 max-w-[34ch] sm:max-w-md mx-auto">
                {t("landing.cta_subtitle")}
              </p>
              <div className="mt-7 sm:mt-8 flex flex-col sm:flex-row justify-center gap-2.5 sm:gap-3 max-w-xs sm:max-w-none mx-auto">
                <Button
                  size="lg"
                  onClick={() => {
                    // Sub-pass 2A (2026-02-17) — the final landing
                    // CTA now mirrors the hero CTA's primary action
                    // and destination.  Both funnel logged-out
                    // visitors into the same analyze-flow starter
                    // (defaults to today's top-opportunity ticker
                    // when available, falls back to NVDA).  A
                    // dedicated source tag keeps analytics able to
                    // tell the two CTAs apart.
                    const tk = firstOpportunity?.ticker || "NVDA";
                    startAnalyze({ navigate, ticker: tk, source: "landing:cta_final_analyze_first" });
                  }}
                  data-testid="landing-cta-signup"
                  className="h-12 sm:h-12 px-6 sm:px-8 text-[15px] sm:text-base font-medium"
                  style={{ boxShadow: "0 16px 36px -12px rgba(49,130,206,0.55), inset 0 1px 0 rgba(255,255,255,0.14)" }}
                >
                  {t("landing.cta_signup_final")}
                </Button>
                <Button
                  size="lg"
                  variant="outline"
                  onClick={() => navigate("/pricing")}
                  data-testid="landing-cta-pricing"
                  className="h-12 sm:h-12 px-6 sm:px-7 text-[15px] sm:text-base font-medium border-border/60 bg-card/30"
                >
                  {t("landing.cta_pricing")}
                </Button>
              </div>
              <div className="mt-6 sm:mt-7 inline-flex flex-wrap justify-center items-center gap-x-3 gap-y-1 text-[11px] sm:text-xs text-muted-foreground/65">
                <span className="inline-flex items-center gap-1.5"><span className="w-1 h-1 rounded-full bg-[#3FAE89]" /> No credit card</span>
                <span className="text-muted-foreground/30">·</span>
                <span className="inline-flex items-center gap-1.5"><span className="w-1 h-1 rounded-full bg-[#3FAE89]" /> Cancel anytime</span>
                <span className="text-muted-foreground/30">·</span>
                <span className="inline-flex items-center gap-1.5"><span className="w-1 h-1 rounded-full bg-[#3FAE89]" /> EU billing</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* WS-1 canonical regulatory disclosure — moved from directly
          under the AI Market Snapshot to a dedicated closing strip so
          the transparency information reads as page-level context and
          is no longer confined to the picks surface. Content and
          semantics preserved (data-ws1-* attributes still present). */}
      <section
        className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 mt-14 sm:mt-20"
        data-testid="landing-transparency"
      >
        <div
          className="rounded-2xl border border-border/60 bg-card/40 px-5 sm:px-6 py-5 sm:py-6"
          data-testid="landing-picks-ws1-disclosure"
          data-ws1-surface="landing_page"
          data-ws1-as-of={asOf || ""}
        >
          <Disclaimer />
          <div
            className="mt-4 sm:mt-5 grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4"
            data-testid="landing-picks-ws1-status-line"
          >
            <div
              className="rounded-lg border border-border/50 bg-black/20 px-3 py-2.5"
              data-ws1-status="methodology"
              data-ws1-value="methodology_active_v1"
              data-testid="landing-transparency-methodology"
            >
              <div className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground/70 font-medium">
                {t("research_transparency.methodology_label")}
              </div>
              <div className="mt-1 text-[12px] leading-[1.5] text-foreground/85">
                {methodologyShortLabel(t, "stockaily.methodology.v1")}
              </div>
            </div>
            <div
              className="rounded-lg border border-border/50 bg-black/20 px-3 py-2.5"
              data-ws1-status="conflicts"
              data-ws1-value="no_compensation_from_issuer"
              data-testid="landing-transparency-conflicts"
            >
              <div className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground/70 font-medium">
                {t("research_transparency.conflicts_label")}
              </div>
              <div className="mt-1 text-[12px] leading-[1.5] text-foreground/85">
                {conflictsLabel(t)}
              </div>
            </div>
            <div
              className="rounded-lg border border-border/50 bg-black/20 px-3 py-2.5"
              data-ws1-status="corrections"
              data-ws1-value="no_corrections_published"
              data-testid="landing-transparency-corrections"
            >
              <div className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground/70 font-medium">
                {t("research_transparency.corrections_label")}
              </div>
              <div className="mt-1 text-[12px] leading-[1.5] text-foreground/85">
                {correctionsLabel(t, null)}
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}

/** Inline (frameless) variant used on mobile — denser, no card shell. */
function TrustInline({ icon: Icon, title, subtitle }) {
  return (
    <li className="flex items-start gap-2.5">
      <span className="inline-flex w-7 h-7 items-center justify-center rounded-lg bg-secondary/50 border border-border/60 shrink-0 mt-0.5">
        <Icon className="w-3.5 h-3.5 text-[#3182CE]" />
      </span>
      <div className="min-w-0">
        <div className="text-[12.5px] font-medium leading-tight">{title}</div>
        <div className="text-[10.5px] text-muted-foreground/80 leading-[1.35] mt-0.5">{subtitle}</div>
      </div>
    </li>
  );
}

function TrustItem({ icon: Icon, title, subtitle }) {
  return (
    <div className="flex items-start gap-2.5 sm:gap-3">
      <span className="inline-flex w-8 h-8 sm:w-9 sm:h-9 items-center justify-center rounded-lg bg-secondary/60 border border-border/70 shrink-0">
        <Icon className="w-4 h-4 text-[#3182CE]" />
      </span>
      <div className="min-w-0">
        <div className="text-[13px] sm:text-sm font-medium leading-tight">{title}</div>
        <div className="text-[11px] sm:text-xs text-muted-foreground leading-[1.4] mt-0.5">{subtitle}</div>
      </div>
    </div>
  );
}

function LegendChip({ color, bg, border, label, desc, testKey }) {
  return (
    <span
      className="inline-flex items-center gap-1.5 sm:gap-2 rounded-full px-2 sm:px-2.5 py-1 text-[10px] sm:text-[11px]"
      style={{ background: bg, border: `1px solid ${border}`, color }}
      data-testid={`legend-chip-${testKey}`}
    >
      <span className="w-1.5 h-1.5 rounded-full" style={{ background: color }} />
      <span className="font-semibold uppercase tracking-wide">{label}</span>
      <span className="hidden sm:inline text-muted-foreground normal-case">·</span>
      <span className="hidden sm:inline text-muted-foreground normal-case">{desc}</span>
    </span>
  );
}

function LegendDot({ color }) {
  return <span className="inline-block w-1.5 h-1.5 rounded-full" style={{ background: color }} aria-hidden />;
}
